import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { FaUpload, FaSave, FaUsers, FaGithub, FaCloudUploadAlt } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

const ROLES_DISPONIBLES = ['admin', 'vendedor', 'operario'];

function UserCard({ user }) {
    const [role, setRole] = useState(user.role);
    const [departamento, setDepartamento] = useState(user.departamento || 'General');
    const [isSaving, setIsSaving] = useState(false);

    const handleUpdate = async () => {
        setIsSaving(true);
        const userDocRef = doc(db, 'users', user.id);
        try {
            await updateDoc(userDocRef, { role, departamento });
            toast.success(`Usuario ${user.displayName} actualizado.`);
        } catch (error) {
            toast.error("No se pudo actualizar el usuario.");
        }
        setIsSaving(false);
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3">
            <div><p className="font-bold text-gray-800">{user.displayName}</p><p className="text-sm text-gray-500">{user.email}</p></div>
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rol</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                    {ROLES_DISPONIBLES.map(rol => <option key={rol} value={rol} className="capitalize">{rol.charAt(0).toUpperCase() + rol.slice(1)}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Departamento</label>
                <select value={departamento} onChange={(e) => setDepartamento(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                    <option value="General">General</option><option value="Madera">Madera</option><option value="Softplay">Softplay</option>
                </select>
            </div>
            <button onClick={handleUpdate} disabled={isSaving} className="w-full mt-2 bg-teal-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-600 disabled:bg-gray-400 flex items-center justify-center gap-2">
                <FaSave /> {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
        </div>
    );
}

function UserRow({ user }) {
    const [role, setRole] = useState(user.role);
    const [departamento, setDepartamento] = useState(user.departamento || 'General');
    const [isSaving, setIsSaving] = useState(false);

    const handleUpdate = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'users', user.id), { role, departamento });
            toast.success(`Usuario ${user.displayName} actualizado.`);
        } catch (error) {
            toast.error("No se pudo actualizar el usuario.");
        }
        setIsSaving(false);
    };

    return (
        <tr className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{user.displayName}</div><div className="text-sm text-gray-500">{user.email}</div></td>
            <td className="px-6 py-4 whitespace-nowrap"><select value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">{ROLES_DISPONIBLES.map(rol => <option key={rol} value={rol} className="capitalize">{rol.charAt(0).toUpperCase() + rol.slice(1)}</option>)}</select></td>
            <td className="px-6 py-4 whitespace-nowrap"><select value={departamento} onChange={(e) => setDepartamento(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm"><option value="General">General</option><option value="Madera">Madera</option><option value="Softplay">Softplay</option></select></td>
            <td className="px-6 py-4 whitespace-nowrap text-center"><button onClick={handleUpdate} disabled={isSaving} className="bg-teal-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-600 disabled:bg-gray-400 flex items-center justify-center gap-2"><FaSave /> {isSaving ? 'Guardando...' : 'Guardar'}</button></td>
        </tr>
    );
}

function ConfiguracionView({ usuario }) {
    const [logoPreview, setLogoPreview] = useState('');
    const [logoBase64, setLogoBase64] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isBackingUp, setIsBackingUp] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!usuario) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const configDocRef = doc(db, 'configuracion', 'empresa');
                const usersQuery = collection(db, 'users');

                const [configSnap, usersSnapshot] = await Promise.all([
                    getDoc(configDocRef),
                    getDocs(usersQuery)
                ]);

                if (configSnap.exists() && configSnap.data().logoBase64) {
                    setLogoPreview(configSnap.data().logoBase64);
                }
                
                const usersData = usersSnapshot.docs.map(userDoc => ({ id: userDoc.id, ...userDoc.data() }));
                setUsers(usersData);

            } catch (error) {
                console.error("Error al cargar datos:", error);
                toast.error("Error al cargar configuración.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [usuario]);

    const handleGithubBackup = async () => {
        setIsBackingUp(true);
        const loadingToast = toast.loading("🤖 Jarvis conectando con GitHub...");
        
        try {
            const response = await fetch('http://localhost:3001/respaldar');
            if (response.ok) {
                toast.success("¡Código respaldado en la nube con éxito!", { id: loadingToast });
            } else {
                throw new Error("El servidor de respaldo no respondió correctamente.");
            }
        } catch (error) {
            toast.error("Asegúrate de que el servidor de Jarvis esté corriendo.", { id: loadingToast });
            console.error(error);
        } finally {
            setIsBackingUp(false);
        }
    };

    const handleFileChange = (e) => { /* Tu lógica de logo */ };
    const handleSave = async () => { /* Tu lógica de logo */ };

    if (loading) return <div className="text-center py-10">Cargando configuración...</div>;
    
    return (
        <div className="h-full flex flex-col p-4 md:p-6 gap-6 max-w-7xl mx-auto w-full">
            <Toaster position="top-center" />
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Configuración del Sistema</h1>
                
                {/* BOTÓN DE RESPALDO GITHUB */}
                <button 
                    onClick={handleGithubBackup}
                    disabled={isBackingUp}
                    className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${isBackingUp ? 'bg-gray-400' : 'bg-[#24292e] hover:bg-black hover:scale-105 active:scale-95'}`}
                >
                    <FaGithub size={20} />
                    {isBackingUp ? 'Sincronizando...' : 'Respaldar en GitHub'}
                    <FaCloudUploadAlt className={isBackingUp ? 'animate-bounce' : ''} />
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* SECCIÓN LOGO */}
                <div className="xl:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h2 className="text-xl font-bold text-gray-700 mb-6 flex items-center gap-2">
                        <FaUpload className="text-teal-500" /> Logo Corporativo
                    </h2>
                    <div className="flex flex-col items-center gap-6">
                        <div className="w-48 h-48 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center bg-gray-50 overflow-hidden">
                            {logoPreview ? <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain p-2" /> : <span className="text-gray-400 text-sm">Sin logo cargado</span>}
                        </div>
                        <div className="w-full space-y-3">
                            <label htmlFor="logo-upload" className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-bold py-3 px-4 rounded-xl hover:bg-gray-200 cursor-pointer transition-colors">
                                <FaUpload /> Seleccionar Imagen
                            </label>
                            <input type="file" id="logo-upload" accept="image/*" className="hidden" onChange={handleFileChange} />
                            <button onClick={handleSave} disabled={isSaving || !logoBase64} className="w-full bg-teal-500 text-white font-bold py-3 px-4 rounded-xl hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center gap-2 shadow-md shadow-teal-100">
                                <FaSave /> {isSaving ? 'Guardando...' : 'Actualizar Logo'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* SECCIÓN USUARIOS */}
                <div className="xl:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h2 className="text-xl font-bold text-gray-700 mb-6 flex items-center gap-2">
                        <FaUsers className="text-teal-500" /> Control de Personal
                    </h2>
                    <div className="hidden md:block overflow-hidden rounded-xl border border-gray-100">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usuario</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Rol</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Departamento</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Acción</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {users.map(u => <UserRow key={u.id} user={u} />)}
                            </tbody>
                        </table>
                    </div>
                    <div className="block md:hidden space-y-4">
                        {users.map(u => <UserCard key={u.id} user={u} />)}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfiguracionView;